import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';

import {
  APPLY_SUPERVISOR_RD_PERCENTAGE,
  GET_BACKGROUND_OPERATIONS,
  GET_SUPERVISOR_RD_ANALYSIS_PREVIEW,
  Report,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyId } from '@neo.tax/shared/hooks/useCurrentCompanyId';
import { makeCsvFileAndUrl } from '@neo.tax/shared/utils';

type GetSupervisorRdAnalysisPreviewQuery = {
  getSupervisorRdAnalysisPreview: Report;
};

type GetSupervisorRdAnalysisPreviewQueryVariables = {
  fiscalYearId: string;
  expenseIds?: string[];
  filter?: Record<string, unknown>;
};

type BackgroundOperation = {
  id: string;
  createdAt: string;
  name: string;
  progressPercent: number;
};

type ApplySupervisorRdPercentageMutation = {
  applySupervisorRdPercentage: BackgroundOperation;
};

type ApplySupervisorRdPercentageMutationVariables = {
  fiscalYearId: string;
  expenseIds?: string[];
  applyToAll?: boolean;
  filter?: Record<string, unknown>;
};

type UseSupervisorRdAnalysisPreviewResult = {
  generatePreview: (expenseIds: string[], applyToAll?: boolean, filter?: Record<string, unknown>) => Promise<void>;
  downloadPreview: () => void;
  applyChanges: (expenseIds: string[], applyToAll?: boolean, filter?: Record<string, unknown>) => Promise<boolean>;
  reset: () => void;
  loading: boolean;
  applying: boolean;
  applied: boolean;
  previewGenerated: boolean;
  previewData: Report | null;
  supervisorCount: number;
  error: string | null;
};

export const useSupervisorRdAnalysisPreview = (fiscalYearId: string): UseSupervisorRdAnalysisPreviewResult => {
  const companyId = useCurrentCompanyId();
  const [previewData, setPreviewData] = useState<Report | null>(null);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  const [csvData, setCsvData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [supervisorCount, setSupervisorCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const { refetch: fetchSupervisorRdAnalysisPreview } = useQuery<
    GetSupervisorRdAnalysisPreviewQuery,
    GetSupervisorRdAnalysisPreviewQueryVariables
  >(GET_SUPERVISOR_RD_ANALYSIS_PREVIEW, { fetchPolicy: 'network-only', skip: true });

  const [applySupervisorRdPercentageMutation, { loading: applying }] = useMutation<
    ApplySupervisorRdPercentageMutation,
    ApplySupervisorRdPercentageMutationVariables
  >(APPLY_SUPERVISOR_RD_PERCENTAGE, {
    refetchQueries: [{ query: GET_BACKGROUND_OPERATIONS, variables: { companyId } }],
    awaitRefetchQueries: true,
  });

  const generatePreview = useCallback(
    async (expenseIds: string[], applyToAll?: boolean, filter?: Record<string, unknown>): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await fetchSupervisorRdAnalysisPreview(
          applyToAll ? { fiscalYearId, filter } : { fiscalYearId, expenseIds },
        );
        const report = data.getSupervisorRdAnalysisPreview;
        setPreviewData(report);
        setPreviewGenerated(true);
        setCsvData(report.resultCsv);
        // Count supervisors from CSV rows (minus header)
        const lines = report.resultCsv.split('\n').filter((line) => line.trim() !== '');
        setSupervisorCount(Math.max(0, lines.length - 1));
      } catch (err) {
        setError(`Failed to generate preview. ${err}`);
      } finally {
        setIsLoading(false);
      }
    },
    [fiscalYearId, fetchSupervisorRdAnalysisPreview],
  );

  const downloadPreview = useCallback((): void => {
    if (!csvData) {
      return;
    }

    const { url } = makeCsvFileAndUrl(csvData);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'supervisor-rd-analysis-preview.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [csvData]);

  const applyChanges = useCallback(
    async (expenseIds: string[], applyToAll?: boolean, filter?: Record<string, unknown>): Promise<boolean> => {
      setError(null);
      try {
        const { data } = await applySupervisorRdPercentageMutation({
          variables: applyToAll ? { fiscalYearId, applyToAll: true, filter } : { fiscalYearId, expenseIds },
        });

        if (!data) {
          throw new Error('Failed to apply supervisor R&D percentage');
        }

        setApplied(true);
        return true;
      } catch (err) {
        setError(`Failed to apply changes. ${err}`);
        return false;
      }
    },
    [fiscalYearId, applySupervisorRdPercentageMutation],
  );

  const reset = useCallback((): void => {
    setPreviewData(null);
    setPreviewGenerated(false);
    setCsvData(null);
    setSupervisorCount(0);
    setError(null);
    setApplied(false);
  }, []);

  return {
    generatePreview,
    downloadPreview,
    applyChanges,
    reset,
    loading: isLoading,
    applying,
    applied,
    previewGenerated,
    previewData,
    supervisorCount,
    error,
  };
};
